import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type {
  FredrickLocalMarketItemSummaryV1,
  FredrickLocalSearchHistoryEntryV1,
} from 'fredrick-local-repository';
import { StatusPill } from '../components/StatusPill';
import { LibraryNav } from '../components/library/LibraryNav';
import { ObservedSpellings } from '../components/library/ObservedSpellings';
import { PriceHistoryChart } from '../components/library/PriceHistoryChart';
import { marketItemLoadKey, valueForRequestKey, type KeyedLoadedValue } from '../lib/keyedLoad';
import { marketItemHref, marketSearchHref, parseMarketItemState } from '../lib/libraryRoutes';
import { formatIntegerMeso, formatObservationRange, formatPrice } from '../lib/marketFormat';
import { localRepository } from '../lib/repository';

interface ItemData {
  readonly item: FredrickLocalMarketItemSummaryV1 | null;
  readonly history: readonly FredrickLocalSearchHistoryEntryV1[];
}

export function MarketItemPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const route = useMemo(() => parseMarketItemState(searchParams), [searchParams]);
  const requestKey = marketItemLoadKey(route.itemKey, route.completeness, route.includeAmbiguous);
  const [loadedData, setLoadedData] = useState<KeyedLoadedValue<ItemData> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => localRepository.subscribe(() => setRefreshToken((value) => value + 1)), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    if (!route.itemKey) {
      setLoadedData({ requestKey, value: { item: null, history: [] } });
      setLoading(false);
      return () => { cancelled = true; };
    }
    void Promise.all([
      localRepository.getMarketItem(route.itemKey),
      localRepository.listSearchHistoryEntries(route.itemKey, {
        completeness: route.completeness,
        includeAmbiguous: route.includeAmbiguous,
      }),
    ]).then(([item, history]) => {
      if (cancelled) return;
      setLoadedData({ requestKey, value: { item, history } });
    }).catch((value: unknown) => {
      if (!cancelled) setError(value instanceof Error ? value.message : 'Could not read this market item.');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [route.itemKey, route.completeness, route.includeAmbiguous, requestKey, refreshToken]);

  function updateFilters(next: { completeness?: 'all' | 'complete' | 'partial'; includeAmbiguous?: boolean }) {
    if (!route.itemKey) return;
    const href = marketItemHref(route.itemKey, {
      completeness: next.completeness ?? route.completeness,
      includeAmbiguous: next.includeAmbiguous ?? route.includeAmbiguous,
    });
    setSearchParams(href.split('?')[1] ?? '');
  }

  const data = valueForRequestKey(loadedData, requestKey);

  if (loading && data === null) return <div className="page-width content-page"><LibraryNav/><section className="empty-state panel"><div className="working centered-working"><span className="spinner"/>Loading item history…</div></section></div>;

  const item = data?.item ?? null;
  if (error && data === null) {
    return <div className="page-width content-page"><LibraryNav/><section className="empty-state panel"><span className="empty-mark">!</span><h1>Could not load item history</h1><p>{error}</p><button className="button primary" type="button" onClick={() => setRefreshToken((value) => value + 1)}>Retry</button></section></div>;
  }
  if (!route.itemKey || (!loading && item === null)) {
    return <div className="page-width content-page"><LibraryNav/><section className="empty-state panel"><span className="empty-mark">?</span><h1>Item not found</h1><p>This opaque local item key does not exist in the current derived market view.</p><Link className="button primary" to="/library">Back to Market</Link></section></div>;
  }

  const history = data?.history ?? [];
  return <div className="page-width content-page">
    <LibraryNav/>
    <div className="detail-back"><Link to="/library">← Back to Market</Link></div>
    {error && <div className="notice bad">{error} <button className="text-button" type="button" onClick={() => setRefreshToken((value) => value + 1)}>Retry</button></div>}
    {item && <>
      <div className="page-heading item-heading">
        <div>
          <p className="eyebrow">Observed market item</p>
          <h1>{item.itemName}</h1>
          <ObservedSpellings spellings={item.observedSpellings}/><p className="market-semantics-note">Prices are per item.</p>
        </div>
      </div>

      {item.trustedLatest === null && <div className="notice warn">No complete, high-confidence search currently defines trusted latest market state for this item.</div>}

      <div className="market-overview item-overview">
        <div><strong>{item.searchCount}</strong><span>all searches</span></div>
        <div><strong>{item.completeSearchCount}</strong><span>complete</span></div>
        <div><strong>{item.partialSearchCount}</strong><span>partial</span></div>
        <div><strong>{item.ambiguousSearchCount}</strong><span>ambiguous</span></div>
      </div>

      {item.trustedLatest && <section className="panel trusted-latest">
        <div><span>Trusted latest observation</span><strong>{formatObservationRange(item.trustedLatest.observedAt, item.trustedLatest.observedAtEnd)}</strong></div>
        <div><span>Median price</span><strong>{item.trustedLatest.prices ? `${formatPrice(item.trustedLatest.prices.median)} meso` : 'No priced listings'}</strong></div>
      </section>}

      <section className="panel history-controls" aria-label="History filters">
        <label><span>Completeness</span><select value={route.completeness} onChange={(event) => updateFilters({ completeness: event.target.value as 'all' | 'complete' | 'partial' })}><option value="complete">Complete</option><option value="partial">Partial</option><option value="all">All</option></select></label>
        <label className="check-row"><input type="checkbox" checked={route.includeAmbiguous} onChange={(event) => updateFilters({ includeAmbiguous: event.target.checked })}/> Include ambiguous grouping</label>
      </section>

      <section className="panel chart-panel">
        <div className="section-heading"><div><p className="eyebrow">Discrete observations</p><h2>Price history</h2></div><span>{history.length} visible search{history.length === 1 ? '' : 'es'}</span></div>
        <PriceHistoryChart entries={history}/>
      </section>

      <section className="history-list" aria-label="Search history">
        {history.length === 0 ? <div className="empty-state panel"><h2>No history matches these filters</h2><p>Widen completeness or include ambiguous grouping to inspect uncertain searches.</p></div>
          : history.map(({ summary, presentPages, expectedPageCount }) => <Link className="history-row panel" key={summary.searchId} to={marketSearchHref(summary.searchId)}>
            <div className="history-main"><strong>{formatObservationRange(summary.observedAt, summary.observedAtEnd)}</strong><div className="state-stack"><StatusPill tone={summary.completeness === 'complete' ? 'good' : 'warn'}>{summary.completeness}</StatusPill><StatusPill tone={summary.groupingState === 'high-confidence' ? 'good' : 'warn'}>{summary.groupingState}</StatusPill></div></div>
            <div className="history-grid">
              <div><span>Pages</span><strong>{presentPages.join(', ') || '—'} / {expectedPageCount}</strong></div>
              <div><span>Results</span><strong>{summary.totalResults}</strong></div>
              <div><span>Listings / units</span><strong>{summary.listingCount} / {formatIntegerMeso(summary.totalUnits)}</strong></div>
              <div><span>Median</span><strong>{summary.prices ? formatPrice(summary.prices.median) : '—'}</strong></div>
              <div><span>Range</span><strong>{summary.prices ? `${formatPrice(summary.prices.min)}–${formatPrice(summary.prices.max)}` : '—'}</strong></div>
            </div>
          </Link>)}
      </section>
    </>}
  </div>;
}
